import { Close, Search } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React from "react";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { MdCloudUpload, MdHelpOutline } from "react-icons/md";
import integrationService from "../../../service/integrationService";
import { toast } from "react-toastify";
import errorHandler from "../../../utils/errorHandler";
import publicService from "../../../service/publicService";
import UnitOfMeasureSelect from "./UnitOfMeasureSelect";

const defaultValues = { businessId: "", entityId: "", file: null, hsnCode: "", isicCode: "", transmissionMode: "", priceUnits: [] };

const integrationSchema = Yup.object({
  entityId: Yup.string().label("Entity ID"),
  businessId: Yup.string().label("Business Name"),
  transmissionMode: Yup.string()
    .oneOf(["File Transmission", "API Transmission"], "Invalid transmission mode")
    .required("Transmission mode is required"),
  file: Yup.mixed()
    .required("Keys is required")
    .test("fileExists", "Keys is required", (value) => {
      return value && value instanceof File;
    })
    .label("Keys"),
  priceUnits: Yup.array()
    .of(Yup.object({ code: Yup.string().required(), name: Yup.string().required() }))
    .min(1, "Select at least one unit of measurement")
    .required(),
});

const ConnectFIRS = ({ isOpen, onClose, currentStep, setCurrentStep, id, integration }) => {
  const [businesses, setBusinesses] = React.useState([]);

  const [isMenu, setIsMenu] = React.useState({
    isEntityIDInfo: null,
    isUploadKeysInfo: null,
  });

  const { handleSubmit, formState, control, watch, setValue, reset } = useForm({
    mode: "all",
    resolver: yupResolver(integrationSchema),
    defaultValues: defaultValues,
  });

  const detailsMutation = useMutation({
    mutationFn: () => integrationService.getEntityDetails(entityData, { integrationId: id, organizationId: integration?.organizationId }),
    onSuccess: ({ data }) => {
      setBusinesses(data?.data?.businesses);
      setValue("businessId", data?.data?.businesses[0]?.id);
    },
    onError: () => setValue("businessId", ""),
  });

  const mutation = useMutation({
    mutationFn: (data) => integrationService.proceed(data, { integrationId: id, organizationId: integration?.organizationId }),
    onSuccess: (data) => {
      toast.success("Business connected successfully...");
      reset(defaultValues);
      setCurrentStep((prev) => prev + 1);
    },
    onError: ({ response }) => errorHandler(response),
  });

  const hscodesQuery = useQuery({
    queryKey: ["hscodes"],
    queryFn: () => publicService.getHSCode({}),
  });

  const serviceCodesQuery = useQuery({
    queryKey: ["isiccodes"],
    queryFn: () => publicService.getServiceCodes({}),
  });

  const entityId = watch("entityId", "").trim();
  let hscodes = hscodesQuery?.data?.data?.data ?? [];
  let serviceCodes = serviceCodesQuery?.data?.data?.data ?? [];

  function save(data) {
    const formData = new FormData();
    formData.append("entityId", data.entityId?.trim());
    formData.append("businessId", data.businessId);
    formData.append("transmissionMode", data.transmissionMode);
    formData.append("file", data.file);
    if (data.hsnCode) formData.append("hsnCode", data.hsnCode);
    if (data.isicCode) formData.append("isicCode", data.isicCode);
    data.priceUnits?.forEach((unit, idx) => formData.append(`priceUnits[${idx}]`, unit?.code));
    mutation.mutate(formData);
  }

  function handleChangeMenu(name, value) {
    setIsMenu((prev) => ({ ...prev, [name]: value }));
  }

  const entityData = { data: { entityId: entityId }, action: "GET_ENTITY_DETAILS" };
  const errors = formState.errors;

  React.useEffect(() => {
    if (!entityId) return;
    const timeoutId = setTimeout(() => detailsMutation.mutate(), 1000);
    return () => clearTimeout(timeoutId);
  }, [entityId]);

  function handleClose() {
    reset(defaultValues);
    onClose();
  }

  if (currentStep !== 2) return null;

  return (
    <Dialog
      onClose={handleClose}
      open={isOpen}
      maxWidth="sm"
      PaperProps={{
        component: "form",
        onSubmit: handleSubmit(save),
        className: "w-100",
        style: { maxWidth: 550 },
      }}
    >
      <React.Fragment>
        <div className="d-flex justify-content-between align-items-center gap-4 p-3 py-2 pe-2">
          <div className="fs-5 fw-medium">Connect to FIRS</div>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>
        <Divider className="bg-secondary-subtle" />
      </React.Fragment>

      <DialogContent>
        <Box mb={3}>
          <label className="d-flex align-items-center">
            <span>
              Entity ID: <span className="text-danger">*</span>{" "}
            </span>
            <IconButton size="small">
              <MdHelpOutline
                onClick={(e) => handleChangeMenu("isEntityIDInfo", e.currentTarget)}
                style={{ color: "#0F83EF", cursor: "pointer", fontSize: 20 }}
              />
            </IconButton>
          </label>
          <Controller
            name="entityId"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  type="text"
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={field.value}
                  placeholder="Enter entity ID"
                  onChange={(e) => field.onChange(e.target.value)}
                  helperText={errors["entityId"]?.message}
                  error={!!errors["entityId"]}
                  onPaste={(e) => {
                    const pastedText = e.clipboardData.getData("text");
                    e.preventDefault();
                    field.onChange(pastedText);
                  }}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            Business Name: <span className="text-danger">*</span>
          </label>
          <Controller
            name="businessId"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["businessId"]}>
                <Select
                  fullWidth
                  variant="outlined"
                  value={field.value}
                  disabled={!businesses.length}
                  onChange={(e) => field.onChange(e.target.value)}
                  displayEmpty
                  inputProps={{ className: "bg-white" }}
                >
                  <MenuItem value="">{!businesses.length && detailsMutation.isPending ? "Loading..." : ""}</MenuItem>
                  {businesses?.map((item, index) => {
                    return (
                      <MenuItem value={item?.id} key={index}>
                        {item?.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors["businessId"] ? <FormHelperText className="">{errors["businessId"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label className="d-flex align-items-center">
            <span>
              Upload Keys: <span className="text-danger">*</span>{" "}
            </span>
            <IconButton size="small">
              <MdHelpOutline
                onClick={(e) => handleChangeMenu("isUploadKeysInfo", e.currentTarget)}
                style={{ color: "#0F83EF", cursor: "pointer", fontSize: 20 }}
              />
            </IconButton>
          </label>
          <Controller
            name="file"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth>
                <TextField
                  type="file"
                  fullWidth
                  size="small"
                  variant="outlined"
                  onChange={(e) => field.onChange(e.target.files[0])}
                  helperText={errors["file"]?.message}
                  error={!!errors["file"]}
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <MdCloudUpload color="#1f53d7" size={26} />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            Transmission Mode: <span className="text-danger">*</span>
          </label>

          <Controller
            name="transmissionMode"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["transmissionMode"]}>
                <Select
                  fullWidth
                  variant="outlined"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  displayEmpty
                  inputProps={{ className: "bg-white" }}
                >
                  <MenuItem value="">
                    Select transmission mode
                  </MenuItem>
                  <MenuItem value="File Transmission">File Transmission</MenuItem>
                  <MenuItem value="API Transmission">API Transmission</MenuItem>
                </Select>

                {errors["transmissionMode"] && (
                  <FormHelperText>
                    {errors["transmissionMode"]?.message}
                  </FormHelperText>
                )}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            HSN-Code:{" "}
          </label>
          <Controller
            name="hsnCode"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["hsnCode"]}>
                <Autocomplete
                  id="hsnCode-autocomplete"
                  options={hscodes}
                  getOptionLabel={(option) => `${option?.description} (${option?.hscode})` || ""}
                  value={hscodes.find((opt) => opt.hscode === field.value) || null}
                  onChange={(e, value) => field.onChange(value ? value.hscode : "")}
                  isOptionEqualToValue={(option, value) => option.hscode === value.hscode}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select HS Code" />}
                />
                {errors["hsnCode"] ? <FormHelperText>{errors["hsnCode"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            ISIC-Code:{" "}
          </label>
          <Controller
            name="isicCode"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["isicCode"]}>
                <Autocomplete
                  id="isicCode-autocomplete"
                  options={serviceCodes}
                  getOptionLabel={(option) => `${option?.description} (${option?.code})` || ""}
                  value={serviceCodes.find((opt) => opt.code === field.value) || null}
                  onChange={(e, value) => field.onChange(value ? value.code : "")}
                  isOptionEqualToValue={(option, value) => option.code === value.code}
                  renderInput={(params) => <TextField {...params} size="small" variant="outlined" placeholder="Select Service Code" />}
                />
                {errors["isicCode"] ? <FormHelperText>{errors["isicCode"]?.message}</FormHelperText> : null}
              </FormControl>
            )}
          />
        </Box>
        <Box mb={3}>
          <label>
            Units of Measurement: <span className="text-danger">*</span>
          </label>
          <Controller
            name="priceUnits"
            control={control}
            render={({ field }) => (
              <FormControl size="small" fullWidth error={!!errors["priceUnits"]}>
                <UnitOfMeasureSelect
                  enabled={isOpen}
                  value={field.value}
                  onChange={(value) => field.onChange(value)}
                  error={!!errors["priceUnits"]}
                  helperText={
                    errors["priceUnits"]?.message ??
                    "The units this business trades in. NRS requires a unit of measurement on every invoice line, and they populate the dropdown in the invoice template."
                  }
                />
              </FormControl>
            )}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ gap: 2 }}>
        <Button variant="contained" color="secondary" type="submit" disabled={mutation.isPending} style={{ minWidth: 120 }}>
          {mutation.isPending ? "Submitting..." : "Proceed"}
        </Button>
      </DialogActions>
      <Popover
        open={Boolean(isMenu.isEntityIDInfo)}
        anchorEl={isMenu.isEntityIDInfo}
        onClose={() => handleChangeMenu("isEntityIDInfo", null)}
      >
        <Box p={3} bgcolor="#26293f" style={{ width: "400px" }}>
          <Typography mb={2} fontSize={18} fontWeight={500} color="#fff">
            Entity ID
          </Typography>
          <Typography mb={3} color="#fff">
            An Entity ID refers to a unique identification number assigned to a taxpayer (individual or organization) upon registration with
            FIRS. To get this, simply navigate to Account &gt; API Integration &gt; Entity ID.
          </Typography>

          <Box display="flex" justifyContent="end">
            <Button
              variant="contained"
              style={{ borderRadius: "50px", background: "#586AEB" }}
              onClick={() => handleChangeMenu("isEntityIDInfo", null)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
      <Popover
        open={Boolean(isMenu.isUploadKeysInfo)}
        anchorEl={isMenu.isUploadKeysInfo}
        onClose={() => handleChangeMenu("isUploadKeysInfo", null)}
      >
        <Box p={3} bgcolor="#26293f" style={{ width: "400px" }}>
          <Typography mb={2} fontSize={18} fontWeight={500} color="#fff">
            Encryption Key
          </Typography>
          <Typography mb={3} color="#fff">
            This is a secure digital file that contain encryption keys or certificates used to digitally sign and authenticate electronic
            invoices. To get this file, simply navigate to Account &gt; API Integration &gt; Download keys. On your FIRS e-invoice dashboard
          </Typography>

          <Box display="flex" justifyContent="end">
            <Button
              variant="contained"
              style={{ borderRadius: "50px", background: "#586AEB" }}
              onClick={() => handleChangeMenu("isUploadKeysInfo", null)}
            >
              Close
            </Button>
          </Box>
        </Box>
      </Popover>
    </Dialog>
  );
};

export default ConnectFIRS;
